import React from "react";

import {
  applyLighting,
  computeNaturalEllipse,
  drawEggTransformed,
} from "../eggLighting";

// Drives the egg-relight canvas. Loads the source PNG once, caches it
// alongside its natural-ellipse fit (computed from the raw image alpha),
// then re-renders the egg + lighting on every input change.
//
// The lighting pass is analytic — it derives the canvas-space ellipse from
// the cached natural ellipse + eggTransform rather than re-scanning the
// rendered silhouette each call. This makes rotation/squish/clipping work
// correctly and lets us skip a full canvas scan to fit a bbox.
// silhouetteRef (optional): a ref that this hook populates with the egg's
// silhouette in canvas-virtual coords whenever the egg re-renders. Shape:
// `{ width, height, data: Uint8Array }`, where data[i] = 1 if that pixel's
// alpha is on-egg (>= 128) and 0 otherwise. Flood fill reads this so the egg
// silhouette acts as a wall — a fill seeded outside the egg can't leak in
// through transparent pixels, and vice versa.
export default function useEggLighting({
  canvasRef,
  eggSrc,
  eggTransform,
  frameW,
  frameH,
  lights,
  silhouetteRef,
}) {
  const imgRef = React.useRef(null);
  const ellipseRef = React.useRef(null);
  const [imgReady, setImgReady] = React.useState(false);

  React.useEffect(() => {
    setImgReady(false);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      imgRef.current = img;
      ellipseRef.current = computeNaturalEllipse(img);
      setImgReady(true);
    };
    img.src = eggSrc;
    return () => {
      img.onload = null;
    };
  }, [eggSrc]);

  React.useEffect(() => {
    if (!imgReady) return;
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img) return;
    if (canvas.width !== frameW) canvas.width = frameW;
    if (canvas.height !== frameH) canvas.height = frameH;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    drawEggTransformed({
      destCtx: ctx,
      sourceImg: img,
      eggTransform,
      frameW,
      frameH,
    });
    applyLighting({
      destCtx: ctx,
      naturalEllipse: ellipseRef.current,
      eggTransform,
      frameW,
      frameH,
      lights,
    });

    if (silhouetteRef) {
      // applyLighting only modifies r/g/b, so reading alpha post-lighting is
      // identical to reading it after drawEggTransformed.
      const pixels = ctx.getImageData(0, 0, frameW, frameH).data;
      const data = new Uint8Array(frameW * frameH);
      for (let i = 0, j = 3; i < data.length; i++, j += 4) {
        if (pixels[j] >= 128) data[i] = 1;
      }
      silhouetteRef.current = { width: frameW, height: frameH, data };
    }
  }, [imgReady, canvasRef, eggTransform, frameW, frameH, lights, silhouetteRef]);
}
